import React, { useRef } from 'react';
import { Form } from 'react-bootstrap';
import type { FieldValues } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import ComponentHelper from '../../../helpers/ui/ComponentHelper/ComponentHelper';
import type { ControlledFieldChildrenProps } from '../types';
import type { InputFieldSpec } from './InputField';

const InputFieldInternal = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  render: {
    field: { value, onChange, ref },
    fieldState: { error }
  },
  formProps: { formId, i18n, formMethods },
  fieldSpec
}: ControlledFieldChildrenProps<
  NameT,
  DataT,
  InputFieldSpec<NameT, DataT>
>): React.ReactElement => {
  const { setValue } = formMethods;
  const { formatMessage, hasMessage, loading: textLoading } = i18n;
  const hasBeenFocused = useRef(false);
  const {
    name,
    inputType,
    attributes,
    focus,
    focusHandler,
    disabled,
    onKeyDown,
    onKeyUp,
    onBlur,
    onMirror,
    onBeforeChange,
    ariaLabel,
    placeholderOverride
  } = fieldSpec;
  const placeHolderKey = `${formId}.${name}.placeholder`;

  if (textLoading) {
    return null;
  }

  return (
    <Form.Control
      aria-describedby={name}
      aria-label={ariaLabel || name}
      name={name}
      as="input"
      type={inputType}
      placeholder={hasMessage(placeHolderKey) ? formatMessage(placeHolderKey) : placeholderOverride}
      ref={(element): void => {
        ref(element);

        if (focus) {
          ComponentHelper.focusHandler(hasBeenFocused, element, focusHandler);
        }
      }}
      onChange={event => {
        let finalValue = event.target.value;
        if (onBeforeChange) {
          finalValue = onBeforeChange(event as React.ChangeEvent<HTMLInputElement>);
        }
        if (onMirror) {
          setValue(onMirror.targetField, onMirror.transformer(finalValue));
        }
        onChange(finalValue);
      }}
      isInvalid={error !== undefined}
      disabled={disabled}
      data-testid={`InputField.${formId}.${name}`}
      onBlur={
        onBlur ? event => onBlur(event as unknown as React.KeyboardEvent, formMethods) : undefined
      }
      onKeyDown={onKeyDown ? event => onKeyDown(event, formMethods) : undefined}
      onKeyUp={onKeyUp ? event => onKeyUp(event, formMethods) : undefined}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...attributes}
      value={value ?? ''}
    />
  );
};

export default InputFieldInternal;
